import { resolveApiBaseUrl } from "./map-runtime-urls.js";
import { currentDataLanguage, currentLocale, languageReady, mapText } from "./map-i18n.js";
import { zoneRgbFromSample } from "./map-overview-facts.js";
import { calculatorLootRowSortValues } from "../js/components/calculator-loot-sort.js";
import { parseCalculatorFishingModesV1 } from "../js/components/calculator-fishing-source.js";
import { parseCalculatorUserOverlayDocumentV4 } from "../js/calculator-user-layer-v4-contract.js";
import { parseCalculatorClaimDateV1 } from "../js/contracts/calculator-claim-date-v1.js";

const DEFAULT_ZONE_LOOT_SUMMARY_PATH = "/api/v1/zone_loot_summary";
const DEFAULT_CALCULATOR_LOOT_CONDITION_CONTEXT_PATH =
  "/api/v1/calculator/loot_condition_context";
const CALCULATOR_LOOT_CONDITION_CONTEXT_RESPONSE_FIELDS = Object.freeze([
  "schemaVersion",
  "resolvedRevision",
  "fishingMastery",
  "fishingLifeskillLevel",
]);

export const ZONE_LOOT_RATE_DISPLAY_BASIS_PERCENT = "percent";
export const ZONE_LOOT_RATE_DISPLAY_BASIS_RAW_WEIGHT = "rawWeight";
export const ZONE_LOOT_RATE_DISPLAY_STATE_KNOWN = "known";
export const ZONE_LOOT_RATE_DISPLAY_STATE_UNKNOWN = "unknown";
export const ZONE_LOOT_RATE_DISPLAY_STATE_UNAVAILABLE = "unavailable";

const ZONE_LOOT_PROVENANCE_TONES = new Set([
  "database",
  "derived",
  "imported",
  "manual",
  "overlay",
  "community",
  "community-presence",
  "rate-known",
  "rate-uncertain",
  "ranking-full",
  "ranking-partial",
  "neutral",
]);
const ZONE_LOOT_PROVENANCE_FACT_KINDS = new Set([
  "groupMembership",
  "groupPresence",
  "groupRate",
  "itemPresence",
  "itemRate",
  "itemQuantity",
]);
const ZONE_LOOT_PROVENANCE_SCOPE_FACT_KINDS = Object.freeze({
  groupPresence: new Set(["groupPresence"]),
  groupRate: new Set(["groupRate"]),
  rowPresence: new Set(["itemPresence", "groupMembership"]),
  rowRate: new Set(["itemRate", "itemQuantity"]),
});
const ZONE_LOOT_PROVENANCE_STATUSES = new Set([
  "effective",
  "overridden",
  "refuted",
  "inactiveCondition",
]);
const ZONE_LOOT_PROVENANCE_SOURCE_KINDS = new Set([
  "structuredSourceFact",
  "sourceTextInference",
  "importedEnrichment",
  "communityClaim",
  "manualEnrichment",
  "user",
]);
const ZONE_LOOT_PROVENANCE_SOURCE_ICONS = new Set([
  "none",
  "source-database",
  "source-community",
  "ring-full",
  "ring-partial",
]);
const ZONE_LOOT_PROVENANCE_OBSERVATION_SCOPES = new Set([
  "rankingFullyContained",
  "rankingPartialIntersection",
]);
const ZONE_LOOT_PROVENANCE_RATE_BASES = new Set([
  "ratio",
  "percent",
  "denormalizedWeight",
  "rawWeight",
  "perCatch",
  "perHour",
]);
const ZONE_LOOT_PROVENANCE_PRESENCE_VALUES = new Set(["present", "absent", "unknown"]);
const ZONE_LOOT_PROVENANCE_MEMBERSHIP_VALUES = new Set(["member", "notMember", "unknown"]);
const ZONE_LOOT_PROVENANCE_CONFIDENCE_VALUES = new Set(["known", "guessed"]);
const ZONE_LOOT_UNAVAILABLE_REASON_CODES = new Set([
  "missingSourceFact",
  "unsupportedSourceVariant",
  "noEquivalentRawWeight",
  "incompleteProvenance",
  "notApplicable",
  "modelSelectionRequired",
]);

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function trimString(value) {
  const normalized = String(value ?? "").trim();
  return normalized || "";
}

function normalizeCatchMethods(value, path) {
  return [...parseCalculatorFishingModesV1(value, path)];
}

const ZONE_LOOT_NUMERIC_CONDITION_SUBJECTS = new Set([
  "mastery",
  "lifeskillLevel",
  "fishingLevel",
]);
const ZONE_LOOT_CONDITION_COMPARISONS = new Set([
  "equal",
  "notEqual",
  "greaterThan",
  "greaterThanOrEqual",
  "lessThan",
  "lessThanOrEqual",
]);
function exactObject(value, path, fields) {
  if (!isPlainObject(value)) {
    throw new TypeError(`${path} must be an object`);
  }
  const allowed = new Set(fields);
  const unknown = Object.keys(value).find((field) => !allowed.has(field));
  if (unknown) {
    throw new TypeError(`${path}.${unknown} is an unknown field`);
  }
  return value;
}

function exactRequiredObject(value, path, fields) {
  const parsed = exactObject(value, path, fields);
  const missing = fields.find((field) => !Object.prototype.hasOwnProperty.call(parsed, field));
  if (missing) {
    throw new TypeError(`${path}.${missing} is missing`);
  }
  return parsed;
}

function enumValue(value, allowed, path) {
  if (!allowed.has(value)) {
    throw new TypeError(`${path} has an unsupported value`);
  }
  return value;
}

function normalizeZoneLootCondition(value, path) {
  const kind = isPlainObject(value) ? value.kind : undefined;
  if (kind === "numeric") {
    const condition = exactRequiredObject(
      value,
      path,
      ["kind", "subject", "comparison", "value"],
    );
    if (typeof condition.value !== "number"
      || !Number.isFinite(condition.value)
      || condition.value < 0) {
      throw new TypeError(`${path}.value must be finite and non-negative`);
    }
    return {
      kind,
      subject: enumValue(
        condition.subject,
        ZONE_LOOT_NUMERIC_CONDITION_SUBJECTS,
        `${path}.subject`,
      ),
      comparison: enumValue(
        condition.comparison,
        ZONE_LOOT_CONDITION_COMPARISONS,
        `${path}.comparison`,
      ),
      value: condition.value,
    };
  }
  throw new TypeError(`${path}.kind must be numeric`);
}

function normalizeZoneLootConditionValues(value, path) {
  return requiredArray(value, path).map((condition, index) =>
    normalizeZoneLootCondition(condition, `${path}[${index}]`));
}

export function normalizeZoneLootConditionSet(value, path = "conditions") {
  if (value?.state === "unconditional") {
    exactRequiredObject(value, path, ["state"]);
    return { state: "unconditional" };
  }
  if (value?.state === "known") {
    const known = exactRequiredObject(value, path, ["state", "values"]);
    const values = normalizeZoneLootConditionValues(known.values, `${path}.values`);
    if (values.length === 0) {
      throw new TypeError(`${path}.values must contain at least one condition`);
    }
    return { state: "known", values };
  }
  if (value?.state === "sourceExpression") {
    const sourceExpression = exactRequiredObject(
      value,
      path,
      ["state", "sourceExpression"],
    );
    return {
      state: "sourceExpression",
      sourceExpression: nonEmptyString(
        sourceExpression.sourceExpression,
        `${path}.sourceExpression`,
      ),
    };
  }
  throw new TypeError(`${path}.state has an unsupported condition state`);
}

export function parseZoneLootRateDisplayBasis(value, path = "rateDisplayBasis") {
  if (
    value === ZONE_LOOT_RATE_DISPLAY_BASIS_PERCENT
    || value === ZONE_LOOT_RATE_DISPLAY_BASIS_RAW_WEIGHT
  ) {
    return value;
  }
  throw new TypeError(`${path} must be percent or rawWeight`);
}

export function parseZoneLootRateDisplayState(value, path = "rateDisplayState") {
  if (value === undefined || value === null || value === "") {
    return ZONE_LOOT_RATE_DISPLAY_STATE_UNKNOWN;
  }
  if (
    value === ZONE_LOOT_RATE_DISPLAY_STATE_KNOWN
    || value === ZONE_LOOT_RATE_DISPLAY_STATE_UNKNOWN
    || value === ZONE_LOOT_RATE_DISPLAY_STATE_UNAVAILABLE
  ) {
    return value;
  }
  throw new TypeError(`${path} must be known, unknown, or unavailable`);
}

export function parseZoneLootProvenanceTone(value, path = "provenanceTone") {
  if (ZONE_LOOT_PROVENANCE_TONES.has(value)) {
    return value;
  }
  throw new TypeError(`${path} has an unsupported provenance tone`);
}

function nonEmptyString(value, path) {
  if (typeof value !== "string" || !value.trim()) {
    throw new TypeError(`${path} must be a non-empty string`);
  }
  return value;
}

function provenanceTraceId(value, path) {
  const traceId = nonEmptyString(value, path);
  if (!/^blake3:[0-9a-f]{64}$/.test(traceId)) {
    throw new TypeError(`${path} must be a canonical blake3 calculator trace ID`);
  }
  return traceId;
}

function nullableString(value, path) {
  if (value === null) {
    return null;
  }
  return nonEmptyString(value, path);
}

function nonNegativeNumber(value, path) {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
    throw new TypeError(`${path} must be finite and non-negative`);
  }
  return value;
}

function normalizeUnavailableReason(value, path) {
  const reason = exactRequiredObject(value, path, ["code", "sourceId", "detailCode"]);
  return {
    code: enumValue(reason.code, ZONE_LOOT_UNAVAILABLE_REASON_CODES, `${path}.code`),
    sourceId: nullableString(reason.sourceId, `${path}.sourceId`),
    detailCode: nullableString(reason.detailCode, `${path}.detailCode`),
  };
}

function normalizeProvenanceRate(value, path) {
  if (value?.state === "known") {
    const rate = exactRequiredObject(value, path, ["state", "value", "basis"]);
    return {
      state: "known",
      value: nonNegativeNumber(rate.value, `${path}.value`),
      basis: enumValue(rate.basis, ZONE_LOOT_PROVENANCE_RATE_BASES, `${path}.basis`),
    };
  }
  if (value?.state === "unknown") {
    exactRequiredObject(value, path, ["state"]);
    return { state: "unknown" };
  }
  if (value?.state === "unavailable") {
    const rate = exactRequiredObject(value, path, ["state", "reason"]);
    return {
      state: "unavailable",
      reason: normalizeUnavailableReason(rate.reason, `${path}.reason`),
    };
  }
  throw new TypeError(`${path}.state has an unsupported rate state`);
}

function normalizeProvenanceQuantity(value, path) {
  if (value?.state === "known") {
    const quantity = exactRequiredObject(value, path, ["state", "minimum", "maximum"]);
    const minimum = Number(quantity.minimum);
    const maximum = Number(quantity.maximum);
    if (!Number.isInteger(minimum) || minimum < 0 || minimum > 4_294_967_295) {
      throw new TypeError(`${path}.minimum must be an unsigned 32-bit integer`);
    }
    if (!Number.isInteger(maximum) || maximum < minimum || maximum > 4_294_967_295) {
      throw new TypeError(`${path}.maximum must be an unsigned 32-bit integer at least minimum`);
    }
    return { state: "known", minimum, maximum };
  }
  if (value?.state === "unknown") {
    exactRequiredObject(value, path, ["state"]);
    return { state: "unknown" };
  }
  if (value?.state === "unavailable") {
    const quantity = exactRequiredObject(value, path, ["state", "reason"]);
    return {
      state: "unavailable",
      reason: normalizeUnavailableReason(quantity.reason, `${path}.reason`),
    };
  }
  throw new TypeError(`${path}.state has an unsupported quantity state`);
}

function normalizeProvenanceValue(value, factKind, path) {
  if (factKind === "groupMembership") {
    const authored = exactRequiredObject(value, path, ["kind", "value"]);
    if (authored.kind !== "membership") {
      throw new TypeError(`${path}.kind must be membership for groupMembership`);
    }
    return {
      kind: "membership",
      value: enumValue(
        authored.value,
        ZONE_LOOT_PROVENANCE_MEMBERSHIP_VALUES,
        `${path}.value`,
      ),
    };
  }
  if (factKind === "groupPresence" || factKind === "itemPresence") {
    const authored = exactRequiredObject(value, path, ["kind", "value"]);
    if (authored.kind !== "presence") {
      throw new TypeError(`${path}.kind must be presence for ${factKind}`);
    }
    return {
      kind: "presence",
      value: enumValue(
        authored.value,
        ZONE_LOOT_PROVENANCE_PRESENCE_VALUES,
        `${path}.value`,
      ),
    };
  }
  if (factKind === "groupRate" || factKind === "itemRate") {
    const authored = exactRequiredObject(value, path, ["kind", "value"]);
    if (authored.kind !== "rate") {
      throw new TypeError(`${path}.kind must be rate for ${factKind}`);
    }
    return {
      kind: "rate",
      value: normalizeProvenanceRate(authored.value, `${path}.value`),
    };
  }
  const authored = exactRequiredObject(value, path, ["kind", "value"]);
  if (authored.kind !== "quantity") {
    throw new TypeError(`${path}.kind must be quantity for itemQuantity`);
  }
  return {
    kind: "quantity",
    value: normalizeProvenanceQuantity(authored.value, `${path}.value`),
  };
}

function normalizeZoneLootProvenanceSourceDate(value, path) {
  if (value === null) {
    return null;
  }
  const sourceDate = exactRequiredObject(value, path, ["kind", "value"]);
  if (sourceDate.kind === "claimDate") {
    return {
      kind: "claimDate",
      value: parseCalculatorClaimDateV1(sourceDate.value, `${path}.value`),
    };
  }
  if (sourceDate.kind === "importedAtUtc") {
    return {
      kind: "importedAtUtc",
      value: nonEmptyString(sourceDate.value, `${path}.value`),
    };
  }
  throw new TypeError(`${path}.kind must be claimDate or importedAtUtc`);
}

export function normalizeZoneLootProvenanceClaims(value, path, scope) {
  const allowedFactKinds = ZONE_LOOT_PROVENANCE_SCOPE_FACT_KINDS[scope];
  if (!allowedFactKinds) {
    throw new TypeError(`${path} has an unsupported provenance scope`);
  }
  if (!Array.isArray(value)) {
    throw new TypeError(`${path} must be an array`);
  }
  const claims = value.map((rawClaim, index) => {
    const claimPath = `${path}[${index}]`;
    const claim = exactRequiredObject(rawClaim, claimPath, [
      "traceId",
      "factKind",
      "status",
      "sourceKind",
      "sourceName",
      "sourceIcon",
      "sourceTone",
      "sourceDate",
      "observationScope",
      "observationCount",
      "value",
      "conditions",
      "comment",
      "confidence",
    ]);
    const factKind = enumValue(
      claim.factKind,
      ZONE_LOOT_PROVENANCE_FACT_KINDS,
      `${claimPath}.factKind`,
    );
    if (!allowedFactKinds.has(factKind)) {
      throw new TypeError(`${claimPath}.factKind is not valid for ${scope}`);
    }
    const conditions = normalizeZoneLootConditionSet(
      claim.conditions,
      `${claimPath}.conditions`,
    );
    const sourceIcon = enumValue(
      claim.sourceIcon,
      ZONE_LOOT_PROVENANCE_SOURCE_ICONS,
      `${claimPath}.sourceIcon`,
    );
    const sourceTone = parseZoneLootProvenanceTone(
      claim.sourceTone,
      `${claimPath}.sourceTone`,
    );
    const observationScope = claim.observationScope === null
      ? null
      : enumValue(
          claim.observationScope,
          ZONE_LOOT_PROVENANCE_OBSERVATION_SCOPES,
          `${claimPath}.observationScope`,
        );
    const observationCount = claim.observationCount === null
      ? null
      : integerInRange(
          claim.observationCount,
          `${claimPath}.observationCount`,
          1,
          4_294_967_295,
        );
    if ((observationScope === null) !== (observationCount === null)) {
      throw new TypeError(
        `${claimPath} observationScope and observationCount must be present together`,
      );
    }
    if (observationScope !== null) {
      if (factKind !== "itemPresence") {
        throw new TypeError(`${claimPath} ranking observation requires itemPresence`);
      }
      const expectedIcon = observationScope === "rankingFullyContained"
        ? "ring-full"
        : "ring-partial";
      const expectedTone = observationScope === "rankingFullyContained"
        ? "ranking-full"
        : "ranking-partial";
      if (sourceIcon !== expectedIcon || sourceTone !== expectedTone) {
        throw new TypeError(
          `${claimPath} observationScope must match sourceIcon and sourceTone`,
        );
      }
    } else if (
      sourceIcon === "ring-full"
      || sourceIcon === "ring-partial"
      || sourceTone === "ranking-full"
      || sourceTone === "ranking-partial"
    ) {
      throw new TypeError(
        `${claimPath} ranking source presentation requires observationScope and observationCount`,
      );
    }
    return {
      traceId: provenanceTraceId(claim.traceId, `${claimPath}.traceId`),
      factKind,
      status: enumValue(
        claim.status,
        ZONE_LOOT_PROVENANCE_STATUSES,
        `${claimPath}.status`,
      ),
      sourceKind: enumValue(
        claim.sourceKind,
        ZONE_LOOT_PROVENANCE_SOURCE_KINDS,
        `${claimPath}.sourceKind`,
      ),
      sourceName: nonEmptyString(claim.sourceName, `${claimPath}.sourceName`),
      sourceIcon,
      sourceTone,
      sourceDate: normalizeZoneLootProvenanceSourceDate(
        claim.sourceDate,
        `${claimPath}.sourceDate`,
      ),
      observationScope,
      observationCount,
      value: normalizeProvenanceValue(claim.value, factKind, `${claimPath}.value`),
      conditions,
      comment: nullableString(claim.comment, `${claimPath}.comment`),
      confidence: claim.confidence === null
        ? null
        : enumValue(
            claim.confidence,
            ZONE_LOOT_PROVENANCE_CONFIDENCE_VALUES,
            `${claimPath}.confidence`,
          ),
    };
  });
  const lanes = new Map();
  claims.forEach((claim, index) => {
    const lane = lanes.get(claim.traceId) ?? [];
    lane.push({ claim, index });
    lanes.set(claim.traceId, lane);
  });
  lanes.forEach((lane, traceId) => {
    const effective = lane.filter(({ claim }) => claim.status === "effective");
    if (effective.length !== 1) {
      throw new TypeError(`${path} trace ${traceId} must contain exactly one effective claim`);
    }
    const factKind = lane[0].claim.factKind;
    if (lane.some(({ claim }) => claim.factKind !== factKind)) {
      throw new TypeError(`${path} trace ${traceId} mixes provenance fact kinds`);
    }
  });
  return claims;
}

function requiredString(value, path) {
  if (typeof value !== "string") {
    throw new TypeError(`${path} must be a string`);
  }
  return value.trim();
}

function nullableTransportString(value, path) {
  if (value === null) {
    return "";
  }
  return requiredString(value, path);
}

function requiredBoolean(value, path) {
  if (typeof value !== "boolean") {
    throw new TypeError(`${path} must be a boolean`);
  }
  return value;
}

function nullableBoolean(value, path) {
  if (value === null || typeof value === "boolean") {
    return value;
  }
  throw new TypeError(`${path} must be a boolean or null`);
}

function integerInRange(value, path, minimum, maximum) {
  if (!Number.isInteger(value) || value < minimum || value > maximum) {
    throw new TypeError(`${path} must be an integer from ${minimum} through ${maximum}`);
  }
  return value;
}

function opaqueTransportId(value, path) {
  if (typeof value !== "string" || !value || value.trim() !== value) {
    throw new TypeError(`${path} must be a non-empty string without surrounding whitespace`);
  }
  return value;
}

function nullableFiniteNumber(value, path) {
  if (value === null) {
    return null;
  }
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new TypeError(`${path} must be a finite number or null`);
  }
  return value;
}

function requiredArray(value, path) {
  if (!Array.isArray(value)) {
    throw new TypeError(`${path} must be an array`);
  }
  return value;
}

function requiredRateDisplayState(value, path) {
  if (typeof value !== "string") {
    throw new TypeError(`${path} must be a string`);
  }
  return parseZoneLootRateDisplayState(value, path);
}

const ZONE_LOOT_SPECIES_FIELDS = Object.freeze([
  "slotIdx",
  "itemId",
  "groupLabel",
  "label",
  "iconUrl",
  "iconGradeTone",
  "isFish",
  "priceRaw",
  "priceText",
  "fillColor",
  "strokeColor",
  "textColor",
  "rateClaims",
  "rawDropRateText",
  "rawDropRateState",
  "rawDropRateTitle",
  "rawDropRateBasis",
  "denormalizedDropRateText",
  "denormalizedDropRateState",
  "denormalizedDropRateTitle",
  "denormalizedDropRateBasis",
  "normalizedDropRateText",
  "normalizedDropRateState",
  "normalizedDropRateTitle",
  "normalizedDropRateBasis",
  "presenceClaims",
  "catchMethods",
  "sortValues",
]);

const ZONE_LOOT_CONDITION_OPTION_FIELDS = Object.freeze([
  "conditionId",
  "conditions",
  "conditionText",
  "conditionTooltip",
  "rateClaims",
  "presenceClaims",
  "rawDropRateText",
  "rawDropRateState",
  "rawDropRateTitle",
  "rawDropRateBasis",
  "denormalizedDropRateText",
  "denormalizedDropRateState",
  "denormalizedDropRateTitle",
  "denormalizedDropRateBasis",
  "normalizedDropRateText",
  "normalizedDropRateState",
  "normalizedDropRateTitle",
  "normalizedDropRateBasis",
  "active",
  "speciesRows",
]);

const ZONE_LOOT_GROUP_FIELDS = Object.freeze([
  "slotIdx",
  "label",
  "fillColor",
  "strokeColor",
  "textColor",
  "rateClaims",
  "presenceClaims",
  "rawDropRateText",
  "rawDropRateState",
  "rawDropRateTitle",
  "rawDropRateBasis",
  "denormalizedDropRateText",
  "denormalizedDropRateState",
  "denormalizedDropRateTitle",
  "denormalizedDropRateBasis",
  "normalizedDropRateText",
  "normalizedDropRateState",
  "normalizedDropRateTitle",
  "normalizedDropRateBasis",
  "conditionText",
  "conditionTooltip",
  "conditionOptionKey",
  "catchMethods",
  "conditionOptions",
]);

const ZONE_LOOT_RESPONSE_FIELDS = Object.freeze([
  "available",
  "zoneName",
  "dataQualityNote",
  "note",
  "profileLabel",
  "groups",
  "speciesRows",
]);

function normalizeZoneLootSpeciesRow(rawRow, path) {
  const row = exactRequiredObject(rawRow, path, ZONE_LOOT_SPECIES_FIELDS);
  return {
    slotIdx: integerInRange(row.slotIdx, `${path}.slotIdx`, 0, 255),
    itemId: integerInRange(row.itemId, `${path}.itemId`, -2_147_483_648, 2_147_483_647),
    groupLabel: requiredString(row.groupLabel, `${path}.groupLabel`),
    label: requiredString(row.label, `${path}.label`),
    iconUrl: nullableTransportString(row.iconUrl, `${path}.iconUrl`),
    iconGradeTone: requiredString(row.iconGradeTone, `${path}.iconGradeTone`),
    isFish: nullableBoolean(row.isFish, `${path}.isFish`),
    priceRaw: nullableFiniteNumber(row.priceRaw, `${path}.priceRaw`),
    priceText: requiredString(row.priceText, `${path}.priceText`),
    fillColor: requiredString(row.fillColor, `${path}.fillColor`),
    strokeColor: requiredString(row.strokeColor, `${path}.strokeColor`),
    textColor: requiredString(row.textColor, `${path}.textColor`),
    rateClaims: normalizeZoneLootProvenanceClaims(
      row.rateClaims,
      `${path}.rateClaims`,
      "rowRate",
    ),
    rawDropRateText: requiredString(row.rawDropRateText, `${path}.rawDropRateText`),
    rawDropRateState: requiredRateDisplayState(
      row.rawDropRateState,
      `${path}.rawDropRateState`,
    ),
    rawDropRateTitle: requiredString(row.rawDropRateTitle, `${path}.rawDropRateTitle`),
    rawDropRateBasis: parseZoneLootRateDisplayBasis(
      row.rawDropRateBasis,
      `${path}.rawDropRateBasis`,
    ),
    denormalizedDropRateText: requiredString(
      row.denormalizedDropRateText,
      `${path}.denormalizedDropRateText`,
    ),
    denormalizedDropRateState: requiredRateDisplayState(
      row.denormalizedDropRateState,
      `${path}.denormalizedDropRateState`,
    ),
    denormalizedDropRateTitle: requiredString(
      row.denormalizedDropRateTitle,
      `${path}.denormalizedDropRateTitle`,
    ),
    denormalizedDropRateBasis: parseZoneLootRateDisplayBasis(
      row.denormalizedDropRateBasis,
      `${path}.denormalizedDropRateBasis`,
    ),
    normalizedDropRateText: requiredString(
      row.normalizedDropRateText,
      `${path}.normalizedDropRateText`,
    ),
    normalizedDropRateState: requiredRateDisplayState(
      row.normalizedDropRateState,
      `${path}.normalizedDropRateState`,
    ),
    normalizedDropRateTitle: requiredString(
      row.normalizedDropRateTitle,
      `${path}.normalizedDropRateTitle`,
    ),
    normalizedDropRateBasis: parseZoneLootRateDisplayBasis(
      row.normalizedDropRateBasis,
      `${path}.normalizedDropRateBasis`,
    ),
    presenceClaims: normalizeZoneLootProvenanceClaims(
      row.presenceClaims,
      `${path}.presenceClaims`,
      "rowPresence",
    ),
    catchMethods: normalizeCatchMethods(row.catchMethods, `${path}.catchMethods`),
    sortValues: calculatorLootRowSortValues(row.sortValues),
  };
}

function normalizeZoneLootConditionOption(rawOption, path) {
  const option = exactRequiredObject(
    rawOption,
    path,
    ZONE_LOOT_CONDITION_OPTION_FIELDS,
  );
  return {
    conditionId: requiredString(option.conditionId, `${path}.conditionId`),
    conditions: normalizeZoneLootConditionSet(option.conditions, `${path}.conditions`),
    conditionText: requiredString(option.conditionText, `${path}.conditionText`),
    conditionTooltip: requiredString(option.conditionTooltip, `${path}.conditionTooltip`),
    rateClaims: normalizeZoneLootProvenanceClaims(
      option.rateClaims,
      `${path}.rateClaims`,
      "groupRate",
    ),
    presenceClaims: normalizeZoneLootProvenanceClaims(
      option.presenceClaims,
      `${path}.presenceClaims`,
      "groupPresence",
    ),
    rawDropRateText: requiredString(option.rawDropRateText, `${path}.rawDropRateText`),
    rawDropRateState: requiredRateDisplayState(
      option.rawDropRateState,
      `${path}.rawDropRateState`,
    ),
    rawDropRateTitle: requiredString(option.rawDropRateTitle, `${path}.rawDropRateTitle`),
    rawDropRateBasis: parseZoneLootRateDisplayBasis(
      option.rawDropRateBasis,
      `${path}.rawDropRateBasis`,
    ),
    denormalizedDropRateText: requiredString(
      option.denormalizedDropRateText,
      `${path}.denormalizedDropRateText`,
    ),
    denormalizedDropRateState: requiredRateDisplayState(
      option.denormalizedDropRateState,
      `${path}.denormalizedDropRateState`,
    ),
    denormalizedDropRateTitle: requiredString(
      option.denormalizedDropRateTitle,
      `${path}.denormalizedDropRateTitle`,
    ),
    denormalizedDropRateBasis: parseZoneLootRateDisplayBasis(
      option.denormalizedDropRateBasis,
      `${path}.denormalizedDropRateBasis`,
    ),
    normalizedDropRateText: requiredString(
      option.normalizedDropRateText,
      `${path}.normalizedDropRateText`,
    ),
    normalizedDropRateState: requiredRateDisplayState(
      option.normalizedDropRateState,
      `${path}.normalizedDropRateState`,
    ),
    normalizedDropRateTitle: requiredString(
      option.normalizedDropRateTitle,
      `${path}.normalizedDropRateTitle`,
    ),
    normalizedDropRateBasis: parseZoneLootRateDisplayBasis(
      option.normalizedDropRateBasis,
      `${path}.normalizedDropRateBasis`,
    ),
    active: requiredBoolean(option.active, `${path}.active`),
    speciesRows: requiredArray(option.speciesRows, `${path}.speciesRows`)
      .map((row, index) => normalizeZoneLootSpeciesRow(
        row,
        `${path}.speciesRows[${index}]`,
      )),
  };
}

function normalizeZoneLootGroup(rawGroup, path) {
  const group = exactRequiredObject(rawGroup, path, ZONE_LOOT_GROUP_FIELDS);
  return {
    slotIdx: integerInRange(group.slotIdx, `${path}.slotIdx`, 0, 255),
    label: requiredString(group.label, `${path}.label`),
    fillColor: requiredString(group.fillColor, `${path}.fillColor`),
    strokeColor: requiredString(group.strokeColor, `${path}.strokeColor`),
    textColor: requiredString(group.textColor, `${path}.textColor`),
    rateClaims: normalizeZoneLootProvenanceClaims(
      group.rateClaims,
      `${path}.rateClaims`,
      "groupRate",
    ),
    presenceClaims: normalizeZoneLootProvenanceClaims(
      group.presenceClaims,
      `${path}.presenceClaims`,
      "groupPresence",
    ),
    rawDropRateText: requiredString(group.rawDropRateText, `${path}.rawDropRateText`),
    rawDropRateState: requiredRateDisplayState(
      group.rawDropRateState,
      `${path}.rawDropRateState`,
    ),
    rawDropRateTitle: requiredString(group.rawDropRateTitle, `${path}.rawDropRateTitle`),
    rawDropRateBasis: parseZoneLootRateDisplayBasis(
      group.rawDropRateBasis,
      `${path}.rawDropRateBasis`,
    ),
    denormalizedDropRateText: requiredString(
      group.denormalizedDropRateText,
      `${path}.denormalizedDropRateText`,
    ),
    denormalizedDropRateState: requiredRateDisplayState(
      group.denormalizedDropRateState,
      `${path}.denormalizedDropRateState`,
    ),
    denormalizedDropRateTitle: requiredString(
      group.denormalizedDropRateTitle,
      `${path}.denormalizedDropRateTitle`,
    ),
    denormalizedDropRateBasis: parseZoneLootRateDisplayBasis(
      group.denormalizedDropRateBasis,
      `${path}.denormalizedDropRateBasis`,
    ),
    normalizedDropRateText: requiredString(
      group.normalizedDropRateText,
      `${path}.normalizedDropRateText`,
    ),
    normalizedDropRateState: requiredRateDisplayState(
      group.normalizedDropRateState,
      `${path}.normalizedDropRateState`,
    ),
    normalizedDropRateTitle: requiredString(
      group.normalizedDropRateTitle,
      `${path}.normalizedDropRateTitle`,
    ),
    normalizedDropRateBasis: parseZoneLootRateDisplayBasis(
      group.normalizedDropRateBasis,
      `${path}.normalizedDropRateBasis`,
    ),
    conditionText: requiredString(group.conditionText, `${path}.conditionText`),
    conditionTooltip: requiredString(group.conditionTooltip, `${path}.conditionTooltip`),
    conditionOptionKey: requiredString(group.conditionOptionKey, `${path}.conditionOptionKey`),
    catchMethods: normalizeCatchMethods(group.catchMethods, `${path}.catchMethods`),
    conditionOptions: requiredArray(group.conditionOptions, `${path}.conditionOptions`)
      .map((option, index) => normalizeZoneLootConditionOption(
        option,
        `${path}.conditionOptions[${index}]`,
      )),
  };
}

function rgbTripletString(rgbU32) {
  return [(rgbU32 >> 16) & 0xff, (rgbU32 >> 8) & 0xff, rgbU32 & 0xff].join(",");
}

function appendQueryParam(path, key, value) {
  const normalizedValue = trimString(value);
  if (!normalizedValue) {
    return path;
  }
  const separator = path.includes("?") ? "&" : "?";
  return `${path}${separator}${encodeURIComponent(key)}=${encodeURIComponent(normalizedValue)}`;
}

function localizedZoneLootSummaryPath() {
  let path = DEFAULT_ZONE_LOOT_SUMMARY_PATH;
  path = appendQueryParam(path, "lang", currentDataLanguage());
  path = appendQueryParam(path, "locale", currentLocale());
  return path;
}

function calculatorLootConditionContextPath(sourceRef) {
  if (sourceRef === null || sourceRef === undefined) {
    return DEFAULT_CALCULATOR_LOOT_CONDITION_CONTEXT_PATH;
  }
  return appendQueryParam(
    DEFAULT_CALCULATOR_LOOT_CONDITION_CONTEXT_PATH,
    "ref",
    opaqueTransportId(sourceRef, "calculator context sourceRef"),
  );
}

export function zoneRgbFromSelection(selection) {
  const zoneRgb = Number.parseInt(selection?.zoneStats?.zoneRgb, 10);
  if (Number.isInteger(zoneRgb) && zoneRgb >= 0) {
    return zoneRgb;
  }
  if (Array.isArray(selection?.layerSamples)) {
    const zoneSample = selection.layerSamples.find(
      (sample) => trimString(sample?.layerId) === "zone_mask",
    );
    return Number.isInteger(zoneRgbFromSample(zoneSample)) ? zoneRgbFromSample(zoneSample) : null;
  }
  return null;
}

export function normalizeZoneLootSummary(raw) {
  const source = exactRequiredObject(raw, "zoneLootSummary", ZONE_LOOT_RESPONSE_FIELDS);
  return {
    available: requiredBoolean(source.available, "zoneLootSummary.available"),
    zoneName: nullableTransportString(source.zoneName, "zoneLootSummary.zoneName"),
    dataQualityNote: requiredString(
      source.dataQualityNote,
      "zoneLootSummary.dataQualityNote",
    ),
    note: requiredString(source.note, "zoneLootSummary.note"),
    profileLabel: requiredString(source.profileLabel, "zoneLootSummary.profileLabel"),
    groups: requiredArray(source.groups, "zoneLootSummary.groups")
      .map((group, index) => normalizeZoneLootGroup(group, `groups[${index}]`)),
    speciesRows: requiredArray(source.speciesRows, "zoneLootSummary.speciesRows")
      .map((row, index) => normalizeZoneLootSpeciesRow(row, `speciesRows[${index}]`)),
  };
}

export function parseCalculatorLootConditionContextResponseV1(raw) {
  const source = exactRequiredObject(
    raw,
    "calculatorLootConditionContext",
    CALCULATOR_LOOT_CONDITION_CONTEXT_RESPONSE_FIELDS,
  );
  if (source.schemaVersion !== 1) {
    throw new TypeError("calculatorLootConditionContext.schemaVersion must be exactly 1");
  }
  return Object.freeze({
    schemaVersion: 1,
    resolvedRevision: opaqueTransportId(
      source.resolvedRevision,
      "calculatorLootConditionContext.resolvedRevision",
    ),
    fishingMastery: integerInRange(
      source.fishingMastery,
      "calculatorLootConditionContext.fishingMastery",
      0,
      4_294_967_295,
    ),
    fishingLifeskillLevel: integerInRange(
      source.fishingLifeskillLevel,
      "calculatorLootConditionContext.fishingLifeskillLevel",
      0,
      65_535,
    ),
  });
}

export async function loadCalculatorLootConditionContextResponseV1({
  fetchImpl = globalThis.fetch,
  locationLike = globalThis.window?.location,
  sourceRef = null,
  signal = undefined,
} = {}) {
  if (typeof fetchImpl !== "function") {
    throw new TypeError("Calculator loot-condition context requires fetch");
  }
  const baseUrl = resolveApiBaseUrl({ locationLike });
  const response = await fetchImpl(
    `${baseUrl}${calculatorLootConditionContextPath(sourceRef)}`,
    {
      method: "GET",
      headers: { Accept: "application/json" },
      signal,
    },
  );
  if (!response?.ok) {
    if (response?.status === 404) {
      throw new Error(
        "Calculator loot-condition context endpoint is unavailable on the current API build.",
      );
    }
    throw new Error(
      `Calculator loot-condition context request failed (${response?.status ?? "unknown"}).`,
    );
  }
  if (typeof response.json !== "function") {
    throw new TypeError("Calculator loot-condition context response must be JSON");
  }
  return parseCalculatorLootConditionContextResponseV1(await response.json());
}

export async function loadZoneLootSummary(
  zoneRgb,
  options = {},
) {
  const {
    fetchImpl = globalThis.fetch,
    locationLike = globalThis.window?.location,
    calculatorOverlayCurrent,
    signal = undefined,
  } = options;
  const normalizedZoneRgb = Number.parseInt(zoneRgb, 10);
  if (!Number.isInteger(normalizedZoneRgb) || normalizedZoneRgb < 0) {
    throw new Error(mapText("zone_loot.error.invalid_zone_rgb"));
  }
  if (!Object.prototype.hasOwnProperty.call(options, "calculatorOverlayCurrent")) {
    throw new TypeError(
      "Zone loot summary requires calculatorOverlayCurrent or explicit null",
    );
  }
  const userOverlay = calculatorOverlayCurrent === null
    ? null
    : parseCalculatorUserOverlayDocumentV4(calculatorOverlayCurrent);
  await languageReady();
  const baseUrl = resolveApiBaseUrl({ locationLike });
  const requestBody = {
    rgb: rgbTripletString(normalizedZoneRgb),
    userOverlay,
  };
  const response = await fetchImpl(`${baseUrl}${localizedZoneLootSummaryPath()}`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(requestBody),
    signal,
  });
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(mapText("zone_loot.error.missing_endpoint"));
    }
    throw new Error(mapText("zone_loot.error.request_failed", { status: response.status }));
  }
  return normalizeZoneLootSummary(await response.json());
}
